import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client/react';
import { Box, Container, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';

import Page from '../Page';
import Loading from '../shared/Loading';
import ServerError from '../error/ServerError';
import { GET_ME } from '../../graphql/me';
import {
  CREATE_GROUP_CALL,
  GET_GROUP_CALL,
  UPDATE_GROUP_CALL,
} from '../../graphql/group_call';
import config from '../../config';
import GroupCallForm from './GroupCallForm';

const normalizeDate = (date) => (date ? new Date(date).toISOString() : null);

const didScheduleChange = (previousGroupCall, groupCall) =>
  normalizeDate(previousGroupCall?.scheduledStartAt) !==
    normalizeDate(groupCall?.scheduledStartAt) ||
  normalizeDate(previousGroupCall?.scheduledEndAt) !==
    normalizeDate(groupCall?.scheduledEndAt) ||
  (previousGroupCall?.timezone || '') !== (groupCall?.timezone || '') ||
  (previousGroupCall?.meetingUrl || '') !== (groupCall?.meetingUrl || '');

const getGroupCallSuccessMessage = (previousGroupCall, groupCall) => {
  if (groupCall?.status === 'CANCELED') {
    return 'Group call canceled. Member notifications will be sent.';
  }

  if (groupCall?.status === 'SCHEDULED') {
    if (!previousGroupCall || previousGroupCall.status !== 'SCHEDULED') {
      return 'Group call scheduled. Member notifications will be sent.';
    }

    if (didScheduleChange(previousGroupCall, groupCall)) {
      return 'Group call rescheduled. Member notifications will be sent.';
    }
  }

  return 'Group call saved.';
};

export default function GroupCallEditor() {
  const { id } = useParams();
  const isNew = id === 'new';
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const {
    loading: meLoading,
    error: meError,
    data: meData,
  } = useQuery(GET_ME);
  const trainerId = meData?.me?._id;

  const {
    loading: groupCallLoading,
    error: groupCallError,
    data,
  } = useQuery(GET_GROUP_CALL, {
    variables: { id },
    skip: isNew,
  });

  const [createGroupCall, { loading: creating }] = useMutation(
    CREATE_GROUP_CALL,
    {
      onError: (error) => {
        enqueueSnackbar(`Error: ${error.message}`, { variant: 'error' });
      },
      onCompleted: (response) => {
        enqueueSnackbar(
          getGroupCallSuccessMessage(null, response.createGroupCall),
          { variant: 'success' }
        );
        navigate(`/trainer/group_calls/${response.createGroupCall._id}`);
      },
    }
  );

  const [updateGroupCall, { loading: updating }] = useMutation(
    UPDATE_GROUP_CALL,
    {
      onError: (error) => {
        enqueueSnackbar(`Error: ${error.message}`, { variant: 'error' });
      },
      onCompleted: (response) => {
        enqueueSnackbar(
          getGroupCallSuccessMessage(
            data?.groupCall,
            response.updateGroupCall
          ),
          { variant: 'success' }
        );
        navigate('/trainer/group_calls');
      },
    }
  );

  const handleSubmit = (input) => {
    if (isNew) {
      createGroupCall({ variables: { input: { ...input, trainerId } } });
      return;
    }

    updateGroupCall({ variables: { id, input } });
  };

  if (meError) {
    return <ServerError message={meError.message || 'Failed to load'} />;
  }
  if (groupCallError) {
    return (
      <ServerError message={groupCallError.message || 'Failed to load'} />
    );
  }
  if (meLoading || groupCallLoading || (!isNew && !data?.groupCall)) {
    return <Loading />;
  }

  return (
    <Page title={`Group Call : ${config.appDisplayName}`}>
      <Container maxWidth={false}>
        <Box mb={3}>
          <Typography variant="h3" color="textPrimary" gutterBottom>
            {isNew ? 'New Group Call' : 'Edit Group Call'}
          </Typography>
        </Box>
        <GroupCallForm
          groupCall={isNew ? { trainerId } : data.groupCall}
          onCancel={() => navigate('/trainer/group_calls')}
          onSubmit={handleSubmit}
          saving={creating || updating}
        />
      </Container>
    </Page>
  );
}
